/**
 * Fetches the paginated list of applied recommendation actions (DONE
 * ActionClicks) for a deal. Pages are kept in cache for 10 min and are
 * not refetched on window focus or remount to keep the history tab cheap.
 *
 * Optional `filters` narrow results by date range and/or applied-by user.
 * Changing any filter value resets to page 1 (filter values are in the queryKey).
 */
import { useInfiniteQuery, keepPreviousData } from '@tanstack/react-query'
import useApiService from 'services/api'
import type { RecommendationHistoryItem } from 'models/recommendationHistory'
import type { DateRangeValue } from './DateRangePicker'

const PAGE_SIZE = 20

/** Optional filters to narrow recommendation history results by date range and/or applied-by user. */
export interface RecommendationHistoryFilters {
  dateRange?: DateRangeValue
  userIds?: number[]
}

export function useDealRecommendationHistory(
  dealId: number,
  filters: RecommendationHistoryFilters = {}
) {
  const api = useApiService()

  const {
    data,
    fetchNextPage,
    hasNextPage,
    isFetchingNextPage,
    isFetching,
    error,
  } = useInfiniteQuery({
    queryKey: [
      'dealRecommendationHistory',
      dealId,
      filters.dateRange?.from?.toISOString() ?? null,
      filters.dateRange?.to?.toISOString() ?? null,
      filters.userIds ?? [],
    ],
    queryFn: ({ pageParam }) => {
      const params: Record<string, string | number | number[]> = {
        page: pageParam,
        limit: PAGE_SIZE,
      }

      if (filters.dateRange?.from) {
        params.applied_after = filters.dateRange.from.toISOString()
      }
      if (filters.dateRange?.to) {
        params.applied_before = filters.dateRange.to.toISOString()
      }
      if (filters.userIds?.length) {
        params.user = filters.userIds
      }

      return api.getDealRecommendationHistory(dealId, params)
    },
    initialPageParam: 1,
    getNextPageParam: (lastPage, allPages) =>
      lastPage.results.length === PAGE_SIZE ? allPages.length + 1 : undefined,
    enabled: !!dealId,
    staleTime: 10 * 60 * 1000,
    refetchOnWindowFocus: false,
    refetchOnMount: false,
    placeholderData: keepPreviousData,
  })

  const items: RecommendationHistoryItem[] =
    data?.pages.flatMap((p) => p.results) ?? []

  return {
    items,
    fetchNextPage,
    hasNextPage,
    isFetchingNextPage,
    isLoading: isFetching && !isFetchingNextPage,
    error,
  }
}
